import {Injectable, NgZone} from '@angular/core';
import {kml} from '@tmcw/togeojson';

import {
  latLngToTileCoords,
  latLngToTilePixel,
  findFeatureAtPoint,
  calculateBounds,
  renderFeature,
  createPopupContent,
  VectorTileLayerData
} from '@app/utils/vector-tile.utils';
import { MapEventsController } from '../controllers/map-events.controller';
import { MapClickEvent } from '../interfaces';
import { Subscription } from 'rxjs';
import {isMapPolygonFillVisible} from '../constants/map-polygon-fill-visibility.state';
import {pointInPolygon} from '@app/utils/polygons.utils';

declare var L: any;
declare var geojsonvt: any;

// Constants
const VECTOR_TILE_CLICK_PRIORITY = 100;
const LARGE_DATASET_THRESHOLD = 5000;
const VERY_LARGE_DATASET_THRESHOLD = 20000;
const TILE_EXTENT = 4096;

// Style constants
const DEFAULT_STYLE = {
  color: '#3388ff',
  weight: 2,
  opacity: 0.8,
  fillColor: '#3388ff',
  fillOpacity: 0.3
} as const;

const SELECTION_STYLE = {
  strokeStyle: '#ff6600',
  fillStyle: 'rgba(255, 102, 0, 0.4)',
  lineWidth: 3
} as const;

const NORMAL_STYLE = {
  strokeStyle: '#3388ff',
  fillStyle: 'rgba(51, 136, 255, 0.3)',
  lineWidth: 1,
  globalAlpha: 0.9
} as const;

const VERY_LARGE_STYLE = {
  strokeStyle: '#3388ff',
  fillStyle: 'rgba(51, 136, 255, 0.3)',
  lineWidth: 0.9,
  globalAlpha: 0.7
} as const;

// GeoJSON-VT default options
const GEOJSON_VT_OPTIONS = {
  maxZoom: 18,
  extent: TILE_EXTENT,
  buffer: 64,
  lineMetrics: false,
  promoteId: null,
  generateId: true,
  indexMaxZoom: 5,
  indexMaxPoints: 100000
} as const;

/** Above {@link dynamicPolygonsPane} (900), below custom {@link markerPane} (950). */
const GEO_FILE_PANE = 'geoFilePane';
const GEO_FILE_PANE_Z_INDEX = 940;

export interface GeoFileLayer {
  id: string;
  name: string;
  type: 'gpkg' | 'geojson' | 'kml';
  layer: any;
  bounds?: any;
  featureCount: number;
  vectorTileData?: VectorTileLayerData;
}

@Injectable({
  providedIn: 'root'
})
export class GeoFileLoaderManager {
  private mapInstance: any = null;
  private eventsController: MapEventsController | null = null;
  private readonly loadedLayers = new Map<string, GeoFileLayer>();
  private selectedFeatureId: number | null = null;
  private currentVectorTileLayer: GeoFileLayer | null = null;
  private readonly subscriptions: Subscription[] = [];

  constructor(private ngZone: NgZone) {}

  /**
   * Initialize the manager with a map instance and events controller
   */
  initialize(mapInstance: any, eventsController?: MapEventsController): void {
    this.mapInstance = mapInstance;
    this.ensureGeoFilePane();

    if (!eventsController) return;

    this.eventsController = eventsController;

    // Register click handler with high priority for direct map clicks
    this.eventsController.registerClickHandler(
      'geo-file-layer-click',
      VECTOR_TILE_CLICK_PRIORITY,
      (event: MapClickEvent) => this.handleMapClick(event),
    );

    // Listen to polygon clicks (from region polygons) to check vector tiles
    this.subscriptions.push(
      this.eventsController.polygonClick$.subscribe((event: any) => {
        const [lat, lng] = event.clickedLatLng ?? [];
        if (lat != null && lng != null) {
          this.handleClickAtCoordinates(lat, lng);
        }
      })
    );
  }

  /** Whether any uploaded geo file layer is on the map. */
  hasLoadedLayers(): boolean {
    return this.loadedLayers.size > 0;
  }

  /** Hit-test uploaded layers at map coordinates (vector tiles or GeoJSON). */
  pickFeatureAt(latlng: { lat: number; lng: number }): { feature: any; layerId: string } | null {
    if (!this.hasLoadedLayers()) {
      return null;
    }

    for (const [layerId, geoFileLayer] of this.loadedLayers) {
      if (geoFileLayer.vectorTileData) {
        const result = this.findFeatureAtLatLng(latlng, geoFileLayer.vectorTileData);
        if (result) {
          return {feature: result.originalFeature, layerId};
        }
        continue;
      }

      const layerGroup = geoFileLayer.layer;
      if (!layerGroup || typeof layerGroup.eachLayer !== 'function') {
        continue;
      }

      let hit: { feature: any; layerId: string } | null = null;
      layerGroup.eachLayer((featureLayer: any) => {
        if (hit) {
          return;
        }
        if (this.featureLayerContainsLatLng(featureLayer, latlng.lat, latlng.lng)) {
          hit = {feature: featureLayer.feature ?? featureLayer, layerId};
        }
      });
      if (hit) {
        return hit;
      }
    }

    return null;
  }

  handleMapClick(event: MapClickEvent): boolean {
    return this.handleClickAtCoordinates(event.lat, event.lng);
  }

  /**
   * Handle click at specific coordinates (popup + selection); returns true when a geo feature was hit.
   */
  private handleClickAtCoordinates(lat: number, lng: number): boolean {
    if (!this.hasLoadedLayers()) {
      return false;
    }

    const result = this.pickFeatureAt({lat, lng});
    if (!result) {
      return false;
    }

    if (result.layerId) {
      const geoFileLayer = this.loadedLayers.get(result.layerId);
      if (geoFileLayer?.vectorTileData) {
        const vtHit = this.findFeatureAtLatLng({lat, lng}, geoFileLayer.vectorTileData);
        if (vtHit) {
          this.selectFeature(vtHit.originalFeature, result.layerId, vtHit.tileFeature?.id);
        }
      }
    }

    const popupContent = createPopupContent(result.feature?.properties ?? {});
    this.eventsController?.showPopup(lat, lng, popupContent);
    this.eventsController?.emitVectorTileFeatureClick(result.feature, result.layerId, {lat, lng});

    return true;
  }

  private featureLayerContainsLatLng(featureLayer: any, lat: number, lng: number): boolean {
    const L = (window as any).L;
    if (!L) {
      return false;
    }

    const ll = L.latLng(lat, lng);
    const point = {lat, lon: lng};

    if (typeof featureLayer.getLatLng === 'function') {
      const center = featureLayer.getLatLng();
      const radiusPx =
        typeof featureLayer.getRadius === 'function'
          ? featureLayer.getRadius()
          : featureLayer.options?.radius ?? 6;
      return center.distanceTo(ll) <= radiusPx + 4;
    }

    if (typeof featureLayer.getLatLngs === 'function') {
      const rings = this.flattenLatLngRings(featureLayer.getLatLngs());
      for (const ring of rings) {
        if (ring.length >= 3 && pointInPolygon(point, ring)) {
          return true;
        }
      }
      return false;
    }

    return Boolean(featureLayer.getBounds?.()?.contains(ll));
  }

  private flattenLatLngRings(latlngs: any): Array<Array<{ lat: number; lon: number }>> {
    if (!latlngs?.length) {
      return [];
    }

    const first = latlngs[0];
    if (first?.lat != null && first?.lng != null) {
      return [
        latlngs.map((c: any) => ({lat: c.lat, lon: c.lng})),
      ];
    }

    const rings: Array<Array<{ lat: number; lon: number }>> = [];
    for (const part of latlngs) {
      rings.push(...this.flattenLatLngRings(part));
    }
    return rings;
  }

  /**
   * Find feature at lat/lng position
   */
  private findFeatureAtLatLng(latlng: { lat: number, lng: number }, data: VectorTileLayerData): { tileFeature: any, originalFeature: any } | null {
    if (!this.mapInstance) return null;

    const zoom = Math.floor(this.mapInstance.getZoom());
    const tileCoords = latLngToTileCoords(latlng.lat, latlng.lng, zoom);
    const tileData = data.tileIndex.getTile(tileCoords.z, tileCoords.x, tileCoords.y);

    if (!tileData?.features?.length) return null;

    const tilePixel = latLngToTilePixel(latlng.lat, latlng.lng, tileCoords.z, tileCoords.x, tileCoords.y);
    return findFeatureAtPoint(tileData.features, tilePixel.x, tilePixel.y, data.featuresById);
  }

  /**
   * Select a feature and update the tile rendering
   */
  selectFeature(feature: any, layerId: string, featureId?: number): void {
    const geoFileLayer = this.loadedLayers.get(layerId);
    if (!geoFileLayer?.vectorTileData) return;

    this.selectedFeatureId = featureId ?? null;
    geoFileLayer.vectorTileData.selectedFeatureId = this.selectedFeatureId;
    this.currentVectorTileLayer = geoFileLayer;

    geoFileLayer.layer?.redraw?.();
  }

  /**
   * Clear selection
   */
  clearSelection(): void {
    if (this.currentVectorTileLayer?.vectorTileData) {
      this.currentVectorTileLayer.vectorTileData.selectedFeatureId = null;
      this.currentVectorTileLayer.layer?.redraw?.();
    }
    this.selectedFeatureId = null;
    this.currentVectorTileLayer = null;
  }

  /**
   * Get currently selected feature
   */
  getSelectedFeature(): any | null {
    if (!this.currentVectorTileLayer?.vectorTileData || this.selectedFeatureId === null) {
      return null;
    }
    return this.currentVectorTileLayer.vectorTileData.featuresById.get(this.selectedFeatureId);
  }

  /**
   * Cleanup when manager is destroyed
   */
  destroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscriptions.length = 0;
    this.eventsController?.unregisterClickHandler('geo-file-layer-click');
    this.removeAllLayers();
  }

  /**
   * Load a file based on its type
   */
  async loadFile(file: File): Promise<GeoFileLayer | null> {
    if (!this.mapInstance) {
      console.error('GeoFileLoaderManager: Map instance not initialized');
      return null;
    }

    const extension = this.getFileExtension(file.name).toLowerCase();
    const loaders: Record<string, () => Promise<GeoFileLayer>> = {
      'geojson': () => this.loadGeoJSON(file),
      'json': () => this.loadGeoJSON(file),
      'kml': () => this.loadKML(file)
    };

    const loader = loaders[extension];
    if (!loader) {
      console.error(`GeoFileLoaderManager: Unsupported file format: ${extension}`);
      return null;
    }

    try {
      return await loader();
    } catch (error) {
      console.error('GeoFileLoaderManager: Error loading file:', error);
      throw error;
    }
  }


  /**
   * Load a GeoJSON file
   */
  async loadGeoJSON(file: File): Promise<GeoFileLayer> {
    const content = await file.text();
    const geojsonData = JSON.parse(content);
    const featureCollection = geojsonData.type === 'FeatureCollection'
      ? geojsonData
      : { type: 'FeatureCollection', features: [geojsonData] };

    return this.createAndAddLayer(file.name, 'geojson', featureCollection);
  }

  /**
   * Load a KML file
   */
  async loadKML(file: File): Promise<GeoFileLayer> {
    const content = await file.text();
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(content, 'text/xml');
    const geojsonData = kml(xmlDoc);
    const featureCollection = geojsonData.type === 'FeatureCollection'
      ? geojsonData
      : { type: 'FeatureCollection', features: [geojsonData] };

    return this.createAndAddLayer(file.name, 'kml', featureCollection);
  }

  /**
   * Create layer and add to map
   */
  private async createAndAddLayer(
    fileName: string,
    type: 'gpkg' | 'geojson' | 'kml',
    featureCollection: any
  ): Promise<GeoFileLayer> {
    const featureCount = featureCollection.features?.length ?? 0;
    const layerId = `${type}_${fileName}_${Date.now()}`;

    const { layer, bounds, vectorTileData } = featureCount > LARGE_DATASET_THRESHOLD
      ? await this.createVectorTileLayer(featureCollection, featureCount)
      : { ...this.createStandardGeoJSONLayer(featureCollection), vectorTileData: undefined };

    this.ensureGeoFilePane();
    layer.addTo(this.mapInstance);
    layer.bringToFront?.();

    if (bounds?.isValid()) {
      this.mapInstance.fitBounds(bounds, { padding: [50, 50] });
    }

    console.log(`Successfully loaded ${featureCount} features from ${fileName}`);

    const geoFileLayer: GeoFileLayer = {
      id: layerId,
      name: fileName,
      type,
      layer,
      bounds,
      featureCount,
      vectorTileData
    };

    this.loadedLayers.set(layerId, geoFileLayer);
    return geoFileLayer;
  }

  /**
   * Create a Vector Tile layer for large datasets
   */
  private async createVectorTileLayer(
    featureCollection: any,
    featureCount: number
  ): Promise<{ layer: any, bounds: any, vectorTileData: VectorTileLayerData }> {
    return this.ngZone.runOutsideAngular(() => {
      const bounds = calculateBounds(featureCollection);
      const isVeryLarge = featureCount > VERY_LARGE_DATASET_THRESHOLD;

      const tileIndex = geojsonvt(featureCollection, {
        ...GEOJSON_VT_OPTIONS,
        tolerance: isVeryLarge ? 5 : 3
      });

      const featuresById = new Map<number, any>(
        featureCollection.features.map((f: any, i: number) => [i, f])
      );

      const vectorTileData: VectorTileLayerData = {
        tileIndex,
        featuresById,
        featureCollection,
        selectedFeatureId: null
      };

      const layer = this.createVectorGridLayer(vectorTileData, isVeryLarge);

      return { layer, bounds, vectorTileData };
    });
  }

  /**
   * Create a custom VectorGrid-like layer using Canvas
   */
  private createVectorGridLayer(vectorTileData: VectorTileLayerData, isVeryLarge: boolean): any {
    const { tileIndex } = vectorTileData;
    const baseStyle = isVeryLarge ? VERY_LARGE_STYLE : NORMAL_STYLE;

    const VectorTileLayer = L.GridLayer.extend({
      createTile: (coords: any) => {
        const tile = document.createElement('canvas');
        const tileSize = 256;
        tile.width = tileSize;
        tile.height = tileSize;
        tile.style.pointerEvents = 'none';

        const ctx = tile.getContext('2d');
        if (!ctx) return tile;

        const tileData = tileIndex.getTile(coords.z, coords.x, coords.y);
        if (!tileData?.features) return tile;

        const scale = tileSize / TILE_EXTENT;
        const { selectedFeatureId } = vectorTileData;

        // Apply base style
        ctx.strokeStyle = baseStyle.strokeStyle;
        ctx.lineWidth = baseStyle.lineWidth;
        ctx.fillStyle = baseStyle.fillStyle;
        ctx.globalAlpha = baseStyle.globalAlpha;

        // Render features (skip selected)
        let selectedFeature: any = null;
        for (const feature of tileData.features) {
          if (selectedFeatureId !== null && feature.id === selectedFeatureId) {
            selectedFeature = feature;
            continue;
          }
          renderFeature(ctx, feature, scale);
        }

        // Render selected feature on top
        if (selectedFeature) {
          renderFeature(ctx, selectedFeature, scale, SELECTION_STYLE);
        }

        return tile;
      }
    });

    return new VectorTileLayer({
      pane: GEO_FILE_PANE,
      tileSize: 256,
      updateWhenZooming: false,
      updateWhenIdle: true,
      keepBuffer: 2
    });
  }

  /**
   * Create a standard GeoJSON layer for smaller datasets
   */
  private createStandardGeoJSONLayer(featureCollection: any): { layer: any, bounds: any } {
    const renderer = L.canvas({ padding: 0.5, pane: GEO_FILE_PANE });

    const layer = L.geoJSON(featureCollection, {
      pane: GEO_FILE_PANE,
      renderer,
      style: () => ({ ...DEFAULT_STYLE, renderer }),
      pointToLayer: (_: any, latlng: any) =>
        L.circleMarker(latlng, { radius: 6, ...DEFAULT_STYLE, renderer, pane: GEO_FILE_PANE }),
      onEachFeature: (feature: any, featureLayer: any) => {
        featureLayer.on('click', (e: any) => {
          L.DomEvent.stopPropagation(e);
          const clickLat = e.latlng?.lat;
          const clickLng = e.latlng?.lng;
          if (clickLat == null || clickLng == null) {
            return;
          }
          const popupContent = createPopupContent(feature.properties ?? {});
          featureLayer.bindPopup(popupContent).openPopup();
          this.eventsController?.emitVectorTileFeatureClick(feature, '', {lat: clickLat, lng: clickLng});
        });
      }
    });

    return { layer, bounds: layer.getBounds() };
  }

  private ensureGeoFilePane(): void {
    if (!this.mapInstance?.getPane || !this.mapInstance?.createPane) {
      return;
    }
    if (this.mapInstance.getPane(GEO_FILE_PANE)) {
      return;
    }
    const pane = this.mapInstance.createPane(GEO_FILE_PANE);
    pane.style.zIndex = String(GEO_FILE_PANE_Z_INDEX);
  }

  // Utility methods
  private getFileExtension(filename: string): string {
    return filename.split('.').pop() ?? '';
  }

  removeLayer(layerId: string): boolean {
    const layer = this.loadedLayers.get(layerId);
    if (!layer || !this.mapInstance) return false;

    this.mapInstance.removeLayer(layer.layer);
    this.loadedLayers.delete(layerId);

    if (this.currentVectorTileLayer?.id === layerId) {
      this.clearSelection();
    }
    return true;
  }

  removeAllLayers(): void {
    this.loadedLayers.forEach(({ layer }) => this.mapInstance?.removeLayer(layer));
    this.loadedLayers.clear();
    this.clearSelection();
  }

  getLoadedLayers(): GeoFileLayer[] {
    return [...this.loadedLayers.values()];
  }

  hasVectorTileLayers(): boolean {
    for (const layer of this.loadedLayers.values()) {
      if (layer.vectorTileData) return true;
    }
    return false;
  }

  /**
   * Re-render canvas vector tiles and restyle GeoJSON polygons after global fill visibility changes.
   */
  redrawForPolygonFillVisibility(): void {
    const fillOn = isMapPolygonFillVisible();
    for (const geo of this.loadedLayers.values()) {
      if (geo.vectorTileData && typeof geo.layer?.redraw === 'function') {
        geo.layer.redraw();
        continue;
      }
      const lyr = geo.layer;
      if (lyr && typeof lyr.eachLayer === 'function') {
        lyr.eachLayer((featureLayer: any) => {
          const gt = featureLayer.feature?.geometry?.type;
          if (gt === 'Polygon' || gt === 'MultiPolygon') {
            featureLayer.setStyle?.({
              ...DEFAULT_STYLE,
              fillOpacity: fillOn ? DEFAULT_STYLE.fillOpacity : 0,
            });
          }
        });
      }
    }
  }

}
